import { useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import toast, { Toaster } from 'react-hot-toast';
import { BriefcaseIcon, CheckCircleIcon, XMarkIcon, DocumentTextIcon } from '@heroicons/react/24/outline';
import publicClient from '../../api/publicClient';
import { DATE_FLOOR, DOB_FLOOR } from '../../lib/dateBounds';

const ROLE_OPTIONS = [
  { value: 'teacher', label: 'Full-Time Teacher' },
  { value: 'admin', label: 'Office Admin' },
  { value: 'float', label: 'Part-Time Float Teacher' },
  { value: 'cook', label: 'Cook (Mon–Fri, 10am–2pm)' },
];

const DAY_KEYS = [
  { key: 'monday', label: 'Monday' },
  { key: 'tuesday', label: 'Tuesday' },
  { key: 'wednesday', label: 'Wednesday' },
  { key: 'thursday', label: 'Thursday' },
  { key: 'friday', label: 'Friday' },
  { key: 'saturday', label: 'Saturday' },
  { key: 'sunday', label: 'Sunday' },
];

const WEEKDAY_KEYS = DAY_KEYS.slice(0, 5);

// Roles for which the candidate-entered availability is editable Mon–Fri only.
const FT_ROLES = ['admin', 'teacher'];

function defaultPatternFor(role) {
  if (role === 'cook') {
    return WEEKDAY_KEYS.reduce((acc, d) => {
      acc[d.key] = { enabled: true, start: '10:00', end: '14:00' };
      return acc;
    }, {});
  }
  if (FT_ROLES.includes(role)) {
    return WEEKDAY_KEYS.reduce((acc, d) => {
      acc[d.key] = { enabled: true, start: '06:00', end: '19:00' };
      return acc;
    }, {});
  }
  // float: every day off by default, candidate enables the ones they can work
  return DAY_KEYS.reduce((acc, d) => {
    acc[d.key] = { enabled: false, start: '09:00', end: '17:00' };
    return acc;
  }, {});
}

function patternForApi(role, uiPattern) {
  const out = {};
  for (const [day, slot] of Object.entries(uiPattern || {})) {
    if (!slot?.enabled) continue;
    if (FT_ROLES.includes(role) && !WEEKDAY_KEYS.some(d => d.key === day)) continue;
    out[day] = { start: slot.start, end: slot.end };
  }
  return out;
}

export default function ApplyPage() {
  const [searchParams] = useSearchParams();
  const embedded = searchParams.get('embedded') === 'true';
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [role, setRole] = useState('teacher');
  const [pattern, setPattern] = useState(() => defaultPatternFor('teacher'));
  const [showJobDesc, setShowJobDesc] = useState(false);
  const [jobDesc, setJobDesc] = useState(null);
  const [loadingDesc, setLoadingDesc] = useState(false);
  const { register, handleSubmit, formState: { errors } } = useForm();

  const visibleDays = useMemo(() => {
    if (role === 'cook' || FT_ROLES.includes(role)) return WEEKDAY_KEYS;
    return DAY_KEYS;
  }, [role]);
  const patternReadOnly = role === 'cook';

  const handleRoleChange = (e) => {
    const next = e.target.value;
    setRole(next);
    setPattern(defaultPatternFor(next));
  };

  const updateDay = (day, changes) => {
    setPattern(prev => ({ ...prev, [day]: { ...prev[day], ...changes } }));
  };

  const openJobDescription = async () => {
    setShowJobDesc(true);
    setLoadingDesc(true);
    setJobDesc(null);
    try {
      const { data } = await publicClient.get(`/api/job-descriptions/public/${role}`);
      setJobDesc(data);
    } catch {
      setJobDesc({ title: ROLE_OPTIONS.find(r => r.value === role)?.label, summary: 'A full description for this role is coming soon. Please reach out if you have questions.' });
    } finally {
      setLoadingDesc(false);
    }
  };

  const onSubmit = async (data) => {
    const apiPattern = patternForApi(role, pattern);
    if (role !== 'cook' && Object.keys(apiPattern).length === 0) {
      toast.error('Please select at least one day of availability');
      return;
    }

    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('first_name', data.first_name);
      formData.append('last_name', data.last_name);
      formData.append('email', data.email);
      formData.append('phone', data.phone || '');
      formData.append('role_applied', role);
      formData.append('date_of_birth', data.date_of_birth);
      formData.append('state_of_birth', (data.state_of_birth || '').toUpperCase());
      if (data.source_detail) formData.append('source_detail', data.source_detail);
      if (data.referred_by) formData.append('referred_by', data.referred_by);
      if (data.earliest_start_date) formData.append('earliest_start_date', data.earliest_start_date);
      formData.append('availability_pattern', JSON.stringify(apiPattern));
      if (data.resume?.[0]) {
        formData.append('resume', data.resume[0]);
      }

      await publicClient.post('/api/apply', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      setSubmitted(true);
    } catch (err) {
      const msg = err.response?.data?.error || 'Something went wrong. Please try again.';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className={`min-h-screen bg-gray-900 flex items-center justify-center ${embedded ? 'p-4' : 'p-6'}`}>
        <Toaster position="top-right" />
        <div className="max-w-md w-full text-center">
          <div className="bg-green-500/10 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6">
            <CheckCircleIcon className="w-12 h-12 text-green-400" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-3">Application Submitted!</h2>
          <p className="text-gray-400 text-lg mb-2">Thanks for your interest in Bright Beginnings.</p>
          <p className="text-gray-500">Check your inbox — we've sent a confirmation email with your next steps.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen bg-gray-900 ${embedded ? 'p-4' : 'p-6'}`}>
      <Toaster position="top-right" />

      {/* Job description modal */}
      {showJobDesc && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-gray-800 rounded-2xl border border-gray-700 max-w-lg w-full max-h-[80vh] overflow-y-auto">
            <div className="flex items-start justify-between p-6 border-b border-gray-700">
              <div>
                <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Job Description</div>
                <h3 className="text-lg font-semibold text-white">
                  {loadingDesc ? 'Loading…' : (jobDesc?.title || ROLE_OPTIONS.find(r => r.value === role)?.label)}
                </h3>
              </div>
              <button onClick={() => setShowJobDesc(false)} className="text-gray-400 hover:text-white">
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              {loadingDesc ? (
                <div className="text-gray-500">Loading…</div>
              ) : (
                <>
                  {jobDesc?.summary && (
                    <p className="text-gray-300 text-sm leading-relaxed">{jobDesc.summary}</p>
                  )}
                  {jobDesc?.responsibilities && (
                    <div>
                      <div className="text-xs text-gray-500 uppercase tracking-wide mb-2">Responsibilities</div>
                      <pre className="text-gray-300 text-sm leading-relaxed whitespace-pre-wrap font-sans">{jobDesc.responsibilities}</pre>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="max-w-lg mx-auto">
        {!embedded && (
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-orange-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <span className="text-white font-bold text-xl">BBC</span>
            </div>
            <h1 className="text-2xl font-bold text-white">Join Our Team</h1>
            <p className="text-gray-400 mt-1">Bright Beginnings Childcare</p>
          </div>
        )}

        <div className="bg-gray-800/50 rounded-2xl border border-gray-700/50 p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-orange-500/10 rounded-xl flex items-center justify-center">
              <BriefcaseIcon className="w-5 h-5 text-orange-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white">Employment Application</h2>
              <p className="text-sm text-gray-400">All fields marked * are required</p>
            </div>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-gray-300 mb-1">First Name *</label>
                <input
                  {...register('first_name', { required: 'First name is required' })}
                  className="w-full bg-gray-700/50 border border-gray-600 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-orange-500"
                  placeholder="First name"
                />
                {errors.first_name && <p className="text-red-400 text-xs mt-1">{errors.first_name.message}</p>}
              </div>
              <div>
                <label className="block text-sm text-gray-300 mb-1">Last Name *</label>
                <input
                  {...register('last_name', { required: 'Last name is required' })}
                  className="w-full bg-gray-700/50 border border-gray-600 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-orange-500"
                  placeholder="Last name"
                />
                {errors.last_name && <p className="text-red-400 text-xs mt-1">{errors.last_name.message}</p>}
              </div>
            </div>

            <div>
              <label className="block text-sm text-gray-300 mb-1">Email Address *</label>
              <input
                {...register('email', {
                  required: 'Email is required',
                  pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Invalid email address' }
                })}
                type="email"
                className="w-full bg-gray-700/50 border border-gray-600 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-orange-500"
                placeholder="you@example.com"
              />
              {errors.email && <p className="text-red-400 text-xs mt-1">{errors.email.message}</p>}
            </div>

            <div>
              <label className="block text-sm text-gray-300 mb-1">Phone Number *</label>
              <input
                {...register('phone', { required: 'Phone number is required' })}
                type="tel"
                className="w-full bg-gray-700/50 border border-gray-600 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-orange-500"
                placeholder="(555) 123-4567"
              />
              {errors.phone && <p className="text-red-400 text-xs mt-1">{errors.phone.message}</p>}
            </div>

            {/* DOB + state of birth — required for iLEAD consent / background check.
                Stacked on mobile (grid-cols-1) so the native date picker doesn't
                overlap the State input; splits to two columns at sm: breakpoint. */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-gray-300 mb-1">Date of Birth *</label>
                <input
                  {...register('date_of_birth', { required: 'Required for background check' })}
                  type="date" min={DOB_FLOOR}
                  className="w-full bg-gray-700/50 border border-gray-600 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-orange-500"
                />
                {errors.date_of_birth && <p className="text-red-400 text-xs mt-1">{errors.date_of_birth.message}</p>}
              </div>
              <div>
                <label className="block text-sm text-gray-300 mb-1">State of Birth *</label>
                <input
                  {...register('state_of_birth', { required: 'Required for background check', maxLength: 2, minLength: 2 })}
                  maxLength={2}
                  placeholder="IN"
                  className="w-full uppercase bg-gray-700/50 border border-gray-600 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-orange-500"
                />
                {errors.state_of_birth && <p className="text-red-400 text-xs mt-1">{errors.state_of_birth.message}</p>}
              </div>
            </div>
            <p className="text-[11px] text-gray-500 -mt-2">
              Both fields are required to schedule your background check, drug screen, and fingerprinting if you move forward.
            </p>

            {/* Role + view-description link */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-sm text-gray-300">Position Applied For *</label>
                <button
                  type="button"
                  onClick={openJobDescription}
                  className="text-xs text-orange-400 hover:text-orange-300 flex items-center gap-1"
                >
                  <DocumentTextIcon className="w-4 h-4" />
                  View job description
                </button>
              </div>
              <select
                value={role}
                onChange={handleRoleChange}
                className="w-full bg-gray-700/50 border border-gray-600 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-orange-500"
              >
                {ROLE_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>

            {/* Availability */}
            <div>
              <label className="block text-sm text-gray-300 mb-2">
                Your Availability *
                {role === 'cook' && <span className="text-gray-500 text-xs ml-2">(Cook hours are fixed)</span>}
                {role === 'float' && <span className="text-gray-500 text-xs ml-2">(Part-time / flexible)</span>}
                {FT_ROLES.includes(role) && <span className="text-gray-500 text-xs ml-2">(Full-time, Monday–Friday)</span>}
              </label>
              <div className="space-y-2">
                {visibleDays.map(({ key, label }) => {
                  const slot = pattern[key] || { enabled: false, start: '09:00', end: '17:00' };
                  const lockEnabled = patternReadOnly || FT_ROLES.includes(role);
                  return (
                    <div key={key} className="flex items-center gap-3 bg-gray-700/30 border border-gray-600/50 rounded-lg px-3 py-2">
                      <label className="flex items-center gap-2 w-32 text-sm text-gray-300 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={slot.enabled}
                          disabled={lockEnabled}
                          onChange={(e) => updateDay(key, { enabled: e.target.checked })}
                          className="accent-orange-500 disabled:opacity-50"
                        />
                        {label}
                      </label>
                      {slot.enabled ? (
                        <div className="flex items-center gap-2 flex-1">
                          <input
                            type="time"
                            value={slot.start}
                            disabled={patternReadOnly}
                            onChange={(e) => updateDay(key, { start: e.target.value })}
                            className="flex-1 bg-gray-800/50 border border-gray-600 rounded px-2 py-1 text-sm text-white focus:outline-none focus:border-orange-500 disabled:opacity-60"
                          />
                          <span className="text-gray-500 text-xs">to</span>
                          <input
                            type="time"
                            value={slot.end}
                            disabled={patternReadOnly}
                            onChange={(e) => updateDay(key, { end: e.target.value })}
                            className="flex-1 bg-gray-800/50 border border-gray-600 rounded px-2 py-1 text-sm text-white focus:outline-none focus:border-orange-500 disabled:opacity-60"
                          />
                        </div>
                      ) : (
                        <span className="text-gray-500 text-sm">Unavailable</span>
                      )}
                    </div>
                  );
                })}
              </div>
              {role === 'float' && (
                <p className="text-xs text-gray-500 mt-2">
                  Check the days you can work and set the hours. The scheduler will treat these as your hard caps.
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm text-gray-300 mb-1">Earliest Start Date</label>
              <input
                {...register('earliest_start_date')}
                type="date" min={DATE_FLOOR}
                className="w-full bg-gray-700/50 border border-gray-600 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-orange-500"
              />
            </div>

            <div>
              <label className="block text-sm text-gray-300 mb-1">How did you hear about BBC?</label>
              <select
                {...register('source_detail')}
                className="w-full bg-gray-700/50 border border-gray-600 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-orange-500"
              >
                <option value="">Select…</option>
                <option>Indeed</option>
                <option>Facebook</option>
                <option>Walk-in</option>
                <option>Referral - Employee</option>
                <option>Referral - Parent</option>
                <option>Google</option>
                <option>Other</option>
              </select>
            </div>

            <div>
              <label className="block text-sm text-gray-300 mb-1">If referred, who referred you?</label>
              <input
                {...register('referred_by')}
                placeholder="Optional"
                className="w-full bg-gray-700/50 border border-gray-600 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-orange-500"
              />
            </div>

            <div>
              <label className="block text-sm text-gray-300 mb-1">Resume (PDF or Image)</label>
              <input
                {...register('resume')}
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                className="w-full bg-gray-700/50 border border-gray-600 rounded-xl px-4 py-3 text-gray-400 file:mr-4 file:py-1 file:px-3 file:rounded-lg file:border-0 file:bg-orange-500/20 file:text-orange-400 file:text-sm file:font-medium hover:file:bg-orange-500/30 focus:outline-none"
              />
              <p className="text-xs text-gray-500 mt-1">Max 5MB • PDF, JPG, or PNG</p>
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="w-full bg-orange-500 hover:bg-orange-600 disabled:bg-orange-500/50 text-white font-semibold py-3 px-6 rounded-xl transition-colors mt-2"
            >
              {submitting ? 'Submitting...' : 'Submit Application'}
            </button>
          </form>
        </div>

        {!embedded && (
          <p className="text-center text-gray-600 text-sm mt-6">
            Bright Beginnings Childcare — Confidential
          </p>
        )}
      </div>
    </div>
  );
}
